import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type { CancelDealReqBody, DealEntity } from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
import { useToast } from '../../../hooks/useToast'

import { dealQueryKey } from './useDealQuery'
import { DEALS_QUERY_KEY_ROOT } from './useDealsQuery'
import { invoicesQueryKey } from './useInvoicesQuery'

interface CancelVariables {
  dealId: string
  body: CancelDealReqBody
}

const MODE_DETAIL: Record<CancelDealReqBody['mode'], string> = {
  end_of_period: 'No more invoices generated; existing scheduled invoices stay live until billed.',
  immediate: 'Billing stopped now; scheduled invoices were voided.',
}

export function useCancelDealMutation(): UseMutationResult<DealEntity, unknown, CancelVariables> {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  return useMutation<DealEntity, unknown, CancelVariables>(
    ({ dealId, body }) => dealsAPI.cancelDeal(dealId, body),
    {
      onSuccess: (deal, { body }) => {
        toast.current?.show({
          severity: 'info',
          summary: `Subscription canceled — ${deal.orderNumber}`,
          detail: MODE_DETAIL[body.mode],
          life: 4000,
        })
        queryClient.invalidateQueries({ queryKey: DEALS_QUERY_KEY_ROOT })
        queryClient.invalidateQueries({ queryKey: dealQueryKey(deal.id) })
        queryClient.invalidateQueries({ queryKey: invoicesQueryKey(deal.id) })
      },
      onError: handleError,
    },
  )
}
